import { ChangeDetectionStrategy, Component, computed, inject, input, signal } from "@angular/core";
import { ReactiveFormsModule } from "@angular/forms";
import { FieldTree } from "@angular/forms/signals";
import { StrategyTargetingType } from "@front/m19-models";
import { MatchType, StrategyDspCreativeType, StrategyType, Targeting } from "@front/m19-api-client";
import { AsinSelectionComponent } from "@m19-board/shared/asin-selection/asin-selection.component";
import { IButtonDirective, ILabelComponent, IRadioComponent, IRadioGroupComponent, ModalService } from "@front/m19-ui";
import { KeywordListManagerComponent } from "@m19-board/shared/keyword-list-manager/keyword-list-manager.component";
import { TranslocoDirective, TranslocoService } from "@jsverse/transloco";
import { Constant } from "@front/m19-services";
import {
  FromBrandSelectionModalComponent,
  FromBrandSelectionModalData,
  SpGroupTargetings,
} from "@m19-board/sponsored-product/sp-page/sp-substrategy-creation/from-brand-selection-modal/from-brand-selection-modal.component";

export interface SpCreateStrategyTargetingsModel {
  targetingType: StrategyTargetingType | null;
  keywords: Targeting[];
  productTargetings: string[];
}

@Component({
  selector: "app-sp-create-strategy-targetings",
  imports: [
    ReactiveFormsModule,
    AsinSelectionComponent,
    IButtonDirective,
    KeywordListManagerComponent,
    TranslocoDirective,
    IRadioGroupComponent,
    ILabelComponent,
    IRadioComponent,
  ],
  host: { class: "block h-full" },
  template: ` <div class="flex h-full flex-col gap-4 pt-4" *transloco="let t">
    <div class="flex justify-between">
      <h4>
        {{ t("common.targetings") }}
      </h4>
      @if (strategyType() === StrategyType.BRAND) {
        <button iButton variant="secondary" size="md" (click)="importFromBrandModal()">
          {{ t("sp-substrategy-creation.import_from_brand") }}
        </button>
      }
    </div>
    @if (strategyType() !== StrategyType.BRAND) {
      <IRadioGroup
        name="targeting"
        *transloco="let t"
        class="flex gap-2"
        [value]="targetings().targetingType().value()"
        (valueChange)="targetings().targetingType().value.set($event!)"
      >
        @for (option of targetingOptions; track option.targetingType) {
          <label
            iLabel
            class="relative w-full max-w-160 rounded-xl border p-4"
            [for]="option.targetingType"
            type="card"
          >
            <div class="flex items-start gap-4">
              <div>
                <span class="type-body-medium">{{ t(option.title) }}</span>
                <p class="text-muted-foreground m-0">{{ t(option.description) }}</p>
              </div>
            </div>
            <IRadio
              [id]="option.targetingType"
              [value]="option.targetingType"
              size="lg"
              class="absolute top-4 right-4"
            />
          </label>
        }
      </IRadioGroup>
    }

    @if (showKeywordTargetings()) {
      <div class="flex min-h-0 flex-1 flex-col gap-2">
        <h5>{{ t("common.keywords") }}</h5>
        <div class="type-label">{{ t("sp-substrategy.kw_per_start", [maxKwTargetingByStrategy]) }}</div>
        <div class="min-h-0 flex-1 pt-2">
          <app-keyword-list-manager
            [importedTargetings]="importedTargetings()"
            (targetingsChanged)="updateKeywords($event)"
            [defaultMatchType]="defaultMatchType()"
          />
        </div>
      </div>
    }
    @if (showProductTargetings()) {
      <div class="flex min-h-0 flex-1 flex-col gap-2">
        <h5>{{ t("common.products") }}</h5>
        <div class="type-label">{{ t("sp-substrategy.asin_per_start", [maxAsinTargetingByStrategy]) }}</div>
        <div class="min-h-0 flex-1 pt-2 pb-1">
          <app-asin-selection
            class="max-h-full min-h-0 w-full"
            [csvExport]="false"
            [allowOtherCatalog]="productTargetingsAllowAsinFromOtherCatalog()"
            [bulkButtonLabel]="t('sp-substrategy-creation.add_competitor_asins')"
            [asinsLimit]="maxAsinTargetingByStrategy"
            [selectedAsins]="targetings().productTargetings().value()"
            (selectedAsinsChange)="targetings().productTargetings().value.set($event)"
          />
        </div>
      </div>
    }
  </div>`,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SpCreateStrategyTargetingsComponent {
  private readonly modalService = inject(ModalService);
  private readonly translocoService = inject(TranslocoService);

  readonly targetings = input.required<FieldTree<SpCreateStrategyTargetingsModel>>();
  readonly strategyType = input.required<StrategyType>();

  readonly StrategyType = StrategyType;
  readonly maxKwTargetingByStrategy = Constant.maxKwTargetingByStrategy;
  readonly maxAsinTargetingByStrategy = Constant.maxAsinTargetingByStrategy;
  readonly Media = StrategyDspCreativeType;

  readonly targetingOptions: {
    targetingType: StrategyTargetingType;
    title: string;
    description: string;
  }[] = [
    {
      targetingType: StrategyTargetingType.PRODUCTS,
      title: "common.products",
      description: "sp-substrategy-creation.target_specific_amazon_products",
    },
    {
      targetingType: StrategyTargetingType.KEYWORDS,
      title: "common.keywords",
      description: "sp-substrategy-creation.target_specific_keywords",
    },
  ];

  importedTargetings = signal<Targeting[]>([]);

  showProductTargetings = computed(
    () =>
      this.strategyType() === StrategyType.BRAND ||
      this.targetings().targetingType().value() === StrategyTargetingType.PRODUCTS,
  );
  showKeywordTargetings = computed(
    () =>
      this.strategyType() === StrategyType.BRAND ||
      this.targetings().targetingType().value() === StrategyTargetingType.KEYWORDS,
  );
  productTargetingsAllowAsinFromOtherCatalog = computed(() => this.strategyType() !== StrategyType.BRAND);
  defaultMatchType = computed(() => (this.strategyType() === StrategyType.BRAND ? MatchType.phrase : MatchType.exact));

  importFromBrandModal() {
    this.modalService
      .openModal<FromBrandSelectionModalData, SpGroupTargetings>(FromBrandSelectionModalComponent, {
        size: "sm",
        modalTitle: this.translocoService.translate("sp-substrategy-creation.import_from_brand"),
        data: {
          matchType: MatchType.phrase,
        },
      })
      .afterClosed()
      .subscribe((targetings) => {
        if (targetings) {
          this.importedTargetings.set(targetings.keywords);
          this.addProductTargeting(targetings.products.map((p) => p.asin));
          this.updateKeywords(targetings.keywords);
        }
      });
  }

  addProductTargeting(asins: string[]) {
    const newList = [...(this.targetings().productTargetings().value() ?? [])];
    for (const asin of asins) {
      if (!newList.includes(asin)) {
        newList.push(asin);
      }
    }
    this.targetings().productTargetings().value.set(newList);
  }

  updateKeywords(keywords: Targeting[]) {
    this.targetings().keywords().value.set(keywords);
  }
}
